import { useState, useMemo, useEffect } from "react";
import { get_sim_list, get_sim_detail, update_sim_label } from "../api/others";
import {
    Box,
    Card,
    CardContent,
    TextField,
    InputAdornment,
    Typography,
    Chip,
    Grid,
    LinearProgress,
} from "@mui/material";
import { Search, Wifi, WifiOff, Cpu } from "lucide-react";

function StatCard({ label, value }) {
    return (
        <Card sx={{ borderRadius: 3, p: 3, bgcolor: "#241b35", borderTop: "4px solid #a855f7", color: "#fff" }}>
            <Typography variant="body2" sx={{ color: "#c4b5fd" }} mb={0.5}>{label}</Typography>
            <Typography variant="h5" fontWeight={700}>{value}</Typography>
        </Card>
    );
}

function SimCard({ sim, lastCheckedLabel, onLabelUpdate }) {
    const isOnline = sim.is_online;
    const remaining = parseFloat(sim.remaining_mb);
    const total = parseFloat(sim.total_mb);
    const hasQuota = !isNaN(remaining) && !isNaN(total) && total > 0;
    const pct = hasQuota ? Math.min(Math.max(((total - remaining) / total) * 100, 0), 100) : 0;

    const isCritical = hasQuota && remaining < 100;
    const isWarning = hasQuota && remaining >= 100 && remaining < 150;
    const barColor = isCritical ? "#ef4444" : isWarning ? "#f59e0b" : "#a855f7";
    const remainingColor = isCritical ? "#ef4444" : isWarning ? "#f59e0b" : "#4ade80";

    const [editing, setEditing] = useState(false);
    const [labelInput, setLabelInput] = useState(sim.label || "");
    const [saving, setSaving] = useState(false);

    const handleSaveLabel = async () => {
        setSaving(true);
        const result = await update_sim_label(sim.iccid, labelInput);
        setSaving(false);
        if (result) {
            onLabelUpdate(sim.iccid, labelInput);
            setEditing(false);
        }
    };

    return (
        <Card sx={{
            borderRadius: 3, p: 3,
            bgcolor: isCritical ? "#2d0f0f" : "#241b35",
            border: `1px solid ${isCritical ? "#ef4444" : isWarning ? "#f59e0b" : "#3f2e5c"}`,
            boxShadow: isCritical ? "0 0 16px rgba(239,68,68,0.4)" : isWarning ? "0 0 10px rgba(245,158,11,0.2)" : "0 2px 8px rgba(0,0,0,0.3)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: 2,
        }}>
            {/* Left: ICCID + remaining MB as hero */}
            <Box sx={{ flex: 1, minWidth: 260 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography fontWeight={700} fontSize={15} sx={{ color: "#fff" }}>
                        SIM: {sim.iccid}
                    </Typography>
                    {isCritical && (
                        <Chip label="LOW" size="small" sx={{ bgcolor: "#ef4444", color: "#fff", fontWeight: 700, height: 20, fontSize: 11 }} />
                    )}
                    {isWarning && (
                        <Chip label="WARNING" size="small" sx={{ bgcolor: "#f59e0b", color: "#fff", fontWeight: 700, height: 20, fontSize: 11 }} />
                    )}
                </Box>

                {/* Hero: remaining MB */}
                <Box display="flex" alignItems="baseline" gap={0.5} mb={1}>
                    <Typography fontWeight={800} fontSize={28} sx={{ color: remainingColor, lineHeight: 1 }}>
                        {hasQuota ? remaining.toFixed(2) : "—"}
                    </Typography>
                    <Typography fontSize={13} sx={{ color: "#9b8bb4" }}>
                        MB left {hasQuota ? `of ${total} MB` : ""}
                    </Typography>
                </Box>

                {/* Inline label edit */}
                {editing ? (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <input
                            value={labelInput}
                            onChange={(e) => setLabelInput(e.target.value)}
                            style={{
                                background: "#150f23", border: "1px solid #a855f7", borderRadius: 6,
                                color: "#fff", padding: "4px 8px", fontSize: 13, outline: "none", flex: 1,
                            }}
                            autoFocus
                        />
                        <button onClick={handleSaveLabel} disabled={saving}
                            style={{ background: "#a855f7", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>
                            {saving ? "Saving..." : "Save"}
                        </button>
                        <button onClick={() => { setEditing(false); setLabelInput(sim.label || ""); }}
                            style={{ background: "transparent", color: "#9b8bb4", border: "1px solid #3f2e5c", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}>
                            Cancel
                        </button>
                    </Box>
                ) : (
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <Typography variant="caption" sx={{ color: "#a78bfa" }}>
                            {sim.label || "No label"}
                        </Typography>
                        <button onClick={() => setEditing(true)}
                            style={{ background: "transparent", border: "none", color: "#a855f7", cursor: "pointer", fontSize: 11, padding: "0 4px" }}>
                            ✏️
                        </button>
                    </Box>
                )}

                {/* Secondary: usage bar */}
                {hasQuota && (
                    <Box>
                        <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 5, height: 6, overflow: "hidden" }}>
                            <Box sx={{ bgcolor: barColor, height: "100%", width: `${pct}%`, borderRadius: 5, transition: "width 0.4s ease" }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: "#9b8bb4" }} mt={0.5} display="block">
                            {pct.toFixed(1)}% used · SMS Used: {sim.sms_used ?? "0"}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Right: online/offline + last checked */}
            <Box sx={{ textAlign: "right", minWidth: 140 }}>
                <Chip
                    size="small"
                    icon={isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
                    label={isOnline ? "ONLINE" : "OFFLINE"}
                    sx={{
                        fontWeight: 600,
                        bgcolor: isOnline ? "#16a34a" : "#dc2626",
                        color: "#fff",
                        "& .MuiChip-icon": { color: "#fff" },
                    }}
                />
                <Typography variant="caption" sx={{ color: "#9b8bb4" }} display="block" mt={1}>
                    Last Checked: {lastCheckedLabel}
                </Typography>
            </Box>
        </Card>
    );
}

// Skeleton card shown while a SIM is still loading
function SimCardSkeleton({ iccid }) {
    return (
        <Card sx={{
            borderRadius: 3, p: 3, bgcolor: "#241b35",
            border: "1px solid #3f2e5c", opacity: 0.5,
            display: "flex", alignItems: "center", gap: 2,
        }}>
            <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={15} sx={{ color: "#fff" }}>
                    SIM: {iccid}
                </Typography>
                <Box sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 5, height: 8, mt: 1.5, width: "60%" }} />
                <Typography variant="caption" sx={{ color: "#9b8bb4" }} mt={0.5} display="block">
                    Fetching data...
                </Typography>
            </Box>
            <Box sx={{ width: 80, height: 28, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 20 }} />
        </Card>
    );
}

export function SIMManagementPage() {
    const [search, setSearch] = useState("");
    const [simList, setSimList] = useState([]);        // just iccid + label
    const [simData, setSimData] = useState({});        // iccid -> full detail
    const [loadedCount, setLoadedCount] = useState(0);
    const [error, setError] = useState(null);
    const [lastChecked, setLastChecked] = useState(null);

    useEffect(() => {
        // Step 1: get the list of ICCIDs fast
        get_sim_list()
            .then((list) => {
                if (!list?.length) return;
                setSimList(list);

                // Step 2: fetch each SIM's detail one by one, rendering as we go
                list.forEach((sim) => {
                    get_sim_detail(sim.iccid)
                        .then((detail) => {
                            if (!detail) return;
                            setSimData((prev) => ({ ...prev, [sim.iccid]: detail }));
                            setLoadedCount((prev) => {
                                const next = prev + 1;
                                if (next === list.length) setLastChecked(new Date());
                                return next;
                            });
                        })
                        .catch(() => {
                            // count it as loaded even if it failed so progress bar completes
                            setLoadedCount((prev) => prev + 1);
                        });
                });
            })
            .catch((err) => setError(err?.message || "Failed to load SIM list"));
    }, []);

    const totalCount = simList.length;
    const progressPct = totalCount > 0 ? (loadedCount / totalCount) * 100 : 0;
    const isLoading = loadedCount < totalCount || totalCount === 0;

    const onlineCount = Object.values(simData).filter((s) => s.is_online).length;
    const offlineCount = Object.values(simData).filter((s) => !s.is_online).length;
    const lowQuotaCount = Object.values(simData).filter((s) => parseFloat(s.remaining_mb) < 50).length;

    const lastCheckedLabel = lastChecked
        ? lastChecked.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : "—";

    const filtered = useMemo(() =>
        simList
            .filter((s) => {
                const q = search.toLowerCase();
                return (
                    (s.iccid ?? "").toLowerCase().includes(q) ||
                    (s.label ?? "").toLowerCase().includes(q)
                );
            })
            .sort((a, b) => {
                const remA = parseFloat(simData[a.iccid]?.remaining_mb ?? Infinity);
                const remB = parseFloat(simData[b.iccid]?.remaining_mb ?? Infinity);
                return remA - remB;
            }),
        [simList, simData, search]
    );

    const handleLabelUpdate = (iccid, newLabel) => {
        setSimList((prev) =>
            prev.map((s) => s.iccid === iccid ? { ...s, label: newLabel } : s)
        );
        setSimData((prev) => ({
            ...prev,
            [iccid]: { ...prev[iccid], label: newLabel },
        }));
    };

    return (
        <Box sx={{ p: 3, width: "100%", bgcolor: "#150f23", minHeight: "100vh" }}>
            <Typography variant="h4" fontWeight={600} mb={3} sx={{ color: "#fff" }}>
                SIM Management
            </Typography>

            {error && (
                <Typography color="error" mb={2}>Failed to load SIM data: {error}</Typography>
            )}

            {/* Stat cards */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                    <StatCard label="Total SIMs" value={totalCount || "—"} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard label="Online" value={onlineCount} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard label="Low Quota (< 100 MB)" value={lowQuotaCount} />
                </Grid>
            </Grid>

            {/* Progress bar — shown only while loading */}
            {totalCount > 0 && isLoading && (
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" sx={{ color: "#c4b5fd" }}>
                            {`Loading SIMs... ${loadedCount} / ${totalCount}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#c4b5fd" }}>
                            {progressPct.toFixed(0)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progressPct}
                        sx={{
                            height: 6,
                            borderRadius: 5,
                            bgcolor: "rgba(255,255,255,0.1)",
                            "& .MuiLinearProgress-bar": { bgcolor: "#a855f7", borderRadius: 5 },
                        }}
                    />
                </Box>
            )}

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search by ICCID or Label"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                        bgcolor: "#241b35", color: "#fff",
                        "& fieldset": { borderColor: "#3f2e5c" },
                        "&:hover fieldset": { borderColor: "#a855f7" },
                        "&.Mui-focused fieldset": { borderColor: "#a855f7" },
                    },
                    "& .MuiInputBase-input::placeholder": { color: "#9ca3af", opacity: 1 },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start"><Search size={18} color="#c4b5fd" /></InputAdornment>
                    ),
                }}
            />

            {/* SIM cards — show skeleton while loading, real card when data arrives */}
            <Box display="flex" flexDirection="column" gap={2}>
                {filtered.map((sim) => {
                    const detail = simData[sim.iccid];
                    return detail
                        ? <SimCard key={sim.iccid} sim={{ ...sim, ...detail }} lastCheckedLabel={lastCheckedLabel} onLabelUpdate={handleLabelUpdate} />
                        : <SimCardSkeleton key={sim.iccid} iccid={sim.iccid} />;
                })}
            </Box>
        </Box>
    );
}